import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function TransactionReversals() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("history");
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [reversalHistory, setReversalHistory] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  
  // Modal states
  const [showReversalModal, setShowReversalModal] = useState(false);
  const [reversalType, setReversalType] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [reversalForm, setReversalForm] = useState({
    reason: "",
    notes: ""
  });
  
  const [ledgerAdjustmentForm, setLedgerAdjustmentForm] = useState({
    accountNumber: "",
    amount: "",
    entryType: "Debit",
    reason: "",
    notes: ""
  });

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const res = await api.get("/Auth/me");
      if (!res.data.isAdmin) {
        navigate("/admin");
        return;
      }
      loadReversalHistory();
    } catch (err) {
      navigate("/login");
    }
  };

  const loadReversalHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Reversal/history?pageSize=50");
      setReversalHistory(res.data);
    } catch (err) {
      console.error("Error loading reversal history:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadWalletTransactions = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Transaction/all?limit=100");
      // Filter out already reversed transactions
      setWalletTransactions(res.data.transactions?.filter(t => t.status !== "Reversed") || []);
    } catch (err) {
      console.error("Error loading transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadIncomes = async () => {
    setLoading(true);
    try {
      const res = await api.get("/CompanyFinance/income?pageSize=100");
      setIncomes(res.data.filter(i => i.status !== "Reversed"));
    } catch (err) {
      console.error("Error loading incomes:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const res = await api.get("/CompanyFinance/expense?pageSize=100");
      setExpenses(res.data.filter(e => e.status !== "Reversed"));
    } catch (err) {
      console.error("Error loading expenses:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadLedgerAccounts = async () => {
    try {
      const res = await api.get("/Accounting/ledger-accounts");
      setLedgerAccounts(res.data);
    } catch (err) {
      console.error("Error loading ledger accounts:", err);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "wallet") loadWalletTransactions();
    else if (tab === "income") loadIncomes();
    else if (tab === "expense") loadExpenses();
    else if (tab === "ledger") loadLedgerAccounts();
    else if (tab === "history") loadReversalHistory();
  };

  const openReversalModal = (type, item) => {
    setReversalType(type);
    setSelectedItem(item);
    setReversalForm({ reason: "", notes: "" });
    setShowReversalModal(true);
  };

  const handleReversal = async () => {
    if (!reversalForm.reason || reversalForm.reason.length < 10) {
      alert("Please provide a reason (at least 10 characters)");
      return;
    }

    if (!window.confirm(`Are you sure you want to reverse this ${reversalType}? This action will create a reversal entry.`)) {
      return;
    }

    setLoading(true);
    try {
      let endpoint = "";
      let payload = {};

      switch (reversalType) {
        case "wallet":
          endpoint = "/Reversal/wallet-transaction";
          payload = {
            transactionId: selectedItem.id,
            reason: reversalForm.reason,
            notes: reversalForm.notes
          };
          break;
        case "income":
          endpoint = "/Reversal/income";
          payload = {
            incomeId: selectedItem.id,
            reason: reversalForm.reason,
            notes: reversalForm.notes
          };
          break;
        case "expense":
          endpoint = "/Reversal/expense";
          payload = {
            expenseId: selectedItem.id,
            reason: reversalForm.reason,
            notes: reversalForm.notes
          };
          break;
        default:
          alert("Invalid reversal type");
          return;
      }

      const res = await api.post(endpoint, payload);
      
      alert(`✅ Reversal successful!\n\nReference: ${res.data.reversalReference}\nAmount: ₦${res.data.amountReversed?.toLocaleString()}`);
      
      setShowReversalModal(false);
      loadReversalHistory();
      
      // Reload the relevant data
      if (reversalType === "wallet") loadWalletTransactions();
      else if (reversalType === "income") loadIncomes();
      else if (reversalType === "expense") loadExpenses();
      
    } catch (err) {
      alert("❌ " + (err.response?.data?.message || "Failed to process reversal"));
    } finally {
      setLoading(false);
    }
  };

  const handleLedgerAdjustment = async (e) => {
    e.preventDefault();
    
    if (!ledgerAdjustmentForm.accountNumber || !ledgerAdjustmentForm.amount || !ledgerAdjustmentForm.reason) {
      alert("Please fill in all required fields");
      return;
    }

    if (ledgerAdjustmentForm.reason.length < 10) {
      alert("Please provide a detailed reason (at least 10 characters)");
      return;
    }

    if (!window.confirm("Are you sure you want to post this ledger adjustment?")) {
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/Reversal/ledger-adjustment", {
        accountNumber: ledgerAdjustmentForm.accountNumber,
        amount: parseFloat(ledgerAdjustmentForm.amount),
        entryType: ledgerAdjustmentForm.entryType,
        reason: ledgerAdjustmentForm.reason,
        notes: ledgerAdjustmentForm.notes
      });

      alert(`✅ Ledger adjustment posted!\n\nReference: ${res.data.reversalReference}`);
      
      setLedgerAdjustmentForm({
        accountNumber: "",
        amount: "",
        entryType: "Debit",
        reason: "",
        notes: ""
      });
      
      loadReversalHistory();
      loadLedgerAccounts();
      
    } catch (err) {
      alert("❌ " + (err.response?.data?.message || "Failed to post adjustment"));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  const formatDate = (date) => new Date(date).toLocaleString("en-NG", { 
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" 
  });

  const styles = {
    container: { padding: "20px", maxWidth: "1400px", margin: "0 auto", fontFamily: "Arial, sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
    title: { fontSize: "24px", fontWeight: "bold", color: "#1a1a2e" },
    backBtn: { padding: "10px 20px", background: "#6c757d", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
    tabs: { display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" },
    tab: { padding: "12px 24px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", transition: "all 0.3s" },
    activeTab: { background: "#dc2626", color: "white" },
    inactiveTab: { background: "#e5e7eb", color: "#374151" },
    card: { background: "white", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    warningCard: { background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: "12px", padding: "15px", marginBottom: "20px" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
    th: { padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: "600", color: "#374151", background: "#f9fafb" },
    td: { padding: "12px 8px", borderBottom: "1px solid #e5e7eb" },
    reverseBtn: { padding: "6px 12px", background: "#dc2626", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
    disabledBtn: { padding: "6px 12px", background: "#d1d5db", color: "#6b7280", border: "none", borderRadius: "6px", cursor: "not-allowed", fontSize: "12px" },
    badge: { padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "500" },
    reversedBadge: { background: "#fee2e2", color: "#dc2626" },
    completedBadge: { background: "#dcfce7", color: "#16a34a" },
    pendingBadge: { background: "#fef3c7", color: "#d97706" },
    form: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" },
    formGroup: { display: "flex", flexDirection: "column", gap: "5px" },
    label: { fontWeight: "600", color: "#374151", fontSize: "14px" },
    input: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px" },
    select: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px" },
    textarea: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", minHeight: "80px", resize: "vertical" },
    submitBtn: { padding: "14px 24px", background: "#dc2626", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", fontSize: "16px" },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modal: { background: "white", borderRadius: "12px", padding: "25px", width: "500px", maxWidth: "90%", maxHeight: "90vh", overflow: "auto" },
    modalTitle: { fontSize: "20px", fontWeight: "bold", marginBottom: "20px", color: "#dc2626" },
    modalFooter: { display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" },
    cancelBtn: { padding: "10px 20px", background: "#e5e7eb", color: "#374151", border: "none", borderRadius: "8px", cursor: "pointer" },
    confirmBtn: { padding: "10px 20px", background: "#dc2626", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🔄 Transaction Reversals</h1>
        <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Back to Admin</button>
      </div>

      {/* Warning */}
      <div style={styles.warningCard}>
        <strong>⚠️ Important:</strong> Reversals create counter-entries to maintain audit trail. Original transactions are marked as "Reversed" but not deleted.
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: "history", label: "📜 Reversal History" },
          { id: "wallet", label: "💳 Wallet Transactions" },
          { id: "income", label: "💵 Income" },
          { id: "expense", label: "💸 Expenses" },
          { id: "ledger", label: "📊 Ledger Adjustment" }
        ].map(tab => (
          <button
            key={tab.id}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.activeTab : styles.inactiveTab) }}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Reversal History Tab */}
      {activeTab === "history" && (
        <div style={styles.card}>
          <h3 style={{ marginBottom: "15px" }}>📜 Reversal History</h3>
          {loading ? (
            <p>Loading...</p>
          ) : reversalHistory.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Reference</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Original Ref</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Reason</th>
                    <th style={styles.th}>Reversed By</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reversalHistory.map(r => (
                    <tr key={r.id}>
                      <td style={styles.td}>{formatDate(r.reversedAt)}</td>
                      <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "11px" }}>{r.reversalReference}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: "#e0e7ff", color: "#4f46e5" }}>
                          {r.reversalType}
                        </span>
                      </td>
                      <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "11px" }}>{r.originalReference || "-"}</td>
                      <td style={{ ...styles.td, fontWeight: "600", color: "#dc2626" }}>{formatCurrency(r.originalAmount)}</td>
                      <td style={{ ...styles.td, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }} title={r.reversalReason}>
                        {r.reversalReason}
                      </td>
                      <td style={styles.td}>{r.reversedByName}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, ...styles.completedBadge }}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No reversals recorded yet</p>
          )}
        </div>
      )}

      {/* Wallet Transactions Tab */}
      {activeTab === "wallet" && (
        <div style={styles.card}>
          <h3 style={{ marginBottom: "15px" }}>💳 Wallet Transactions (Available for Reversal)</h3>
          {loading ? (
            <p>Loading...</p>
          ) : walletTransactions.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Reference</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {walletTransactions.map(t => (
                    <tr key={t.id}>
                      <td style={styles.td}>{formatDate(t.createdAt)}</td>
                      <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "11px" }}>{t.reference}</td>
                      <td style={styles.td}>{t.transactionType}</td>
                      <td style={{ ...styles.td, fontWeight: "600", color: t.transactionType?.includes("Deposit") ? "#16a34a" : "#dc2626" }}>
                        {formatCurrency(t.amount)}
                      </td>
                      <td style={styles.td}>{t.description}</td>
                      <td style={styles.td}>
                        <span style={{ 
                          ...styles.badge, 
                          ...(t.status === "Reversed" ? styles.reversedBadge : 
                              t.status === "Completed" ? styles.completedBadge : styles.pendingBadge) 
                        }}>
                          {t.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {t.status === "Completed" ? (
                          <button style={styles.reverseBtn} onClick={() => openReversalModal("wallet", t)}>
                            🔄 Reverse
                          </button>
                        ) : (
                          <button style={styles.disabledBtn} disabled>
                            {t.status === "Reversed" ? "Reversed" : "N/A"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No transactions available for reversal</p>
          )}
        </div>
      )}

      {/* Income Tab */}
      {activeTab === "income" && (
        <div style={styles.card}>
          <h3 style={{ marginBottom: "15px" }}>💵 Income (Available for Reversal)</h3>
          {loading ? (
            <p>Loading...</p>
          ) : incomes.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Posted By</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {incomes.map(i => (
                    <tr key={i.id}>
                      <td style={styles.td}>{formatDate(i.transactionDate)}</td>
                      <td style={styles.td}>{i.category}</td>
                      <td style={{ ...styles.td, fontWeight: "600", color: "#16a34a" }}>{formatCurrency(i.amount)}</td>
                      <td style={styles.td}>{i.description}</td>
                      <td style={styles.td}>{i.postedByName}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, ...(i.status === "Reversed" ? styles.reversedBadge : styles.completedBadge) }}>
                          {i.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {i.status === "Completed" ? (
                          <button style={styles.reverseBtn} onClick={() => openReversalModal("income", i)}>
                            🔄 Reverse
                          </button>
                        ) : (
                          <button style={styles.disabledBtn} disabled>Reversed</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No income records available for reversal</p>
          )}
        </div>
      )}

      {/* Expense Tab */}
      {activeTab === "expense" && (
        <div style={styles.card}>
          <h3 style={{ marginBottom: "15px" }}>💸 Expenses (Available for Reversal)</h3>
          {loading ? (
            <p>Loading...</p>
          ) : expenses.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Vendor</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(e => (
                    <tr key={e.id}>
                      <td style={styles.td}>{formatDate(e.transactionDate)}</td>
                      <td style={styles.td}>{e.category}</td>
                      <td style={{ ...styles.td, fontWeight: "600", color: "#dc2626" }}>{formatCurrency(e.amount)}</td>
                      <td style={styles.td}>{e.vendor || "-"}</td>
                      <td style={styles.td}>{e.description}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, ...(e.status === "Reversed" ? styles.reversedBadge : styles.completedBadge) }}>
                          {e.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {e.status === "Completed" ? (
                          <button style={styles.reverseBtn} onClick={() => openReversalModal("expense", e)}>
                            🔄 Reverse
                          </button>
                        ) : (
                          <button style={styles.disabledBtn} disabled>Reversed</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No expense records available for reversal</p>
          )}
        </div>
      )}

      {/* Ledger Adjustment Tab */}
      {activeTab === "ledger" && (
        <div style={styles.card}>
          <h3 style={{ marginBottom: "20px", color: "#dc2626" }}>📊 Manual Ledger Adjustment</h3>
          <div style={styles.warningCard}>
            <strong>⚠️ Use with caution:</strong> Manual adjustments should only be used for corrections. All adjustments are logged for audit purposes.
          </div>
          <form onSubmit={handleLedgerAdjustment}>
            <div style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Ledger Account *</label>
                <select
                  style={styles.select}
                  value={ledgerAdjustmentForm.accountNumber}
                  onChange={(e) => setLedgerAdjustmentForm({ ...ledgerAdjustmentForm, accountNumber: e.target.value })}
                  required
                >
                  <option value="">Select Account</option>
                  {ledgerAccounts.map(acc => (
                    <option key={acc.accountNumber} value={acc.accountNumber}>
                      {acc.accountNumber} - {acc.accountName} (₦{acc.balance?.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (₦) *</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="0.00"
                  value={ledgerAdjustmentForm.amount}
                  onChange={(e) => setLedgerAdjustmentForm({ ...ledgerAdjustmentForm, amount: e.target.value })}
                  required
                  min="0.01"
                  step="0.01"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Entry Type *</label>
                <select
                  style={styles.select}
                  value={ledgerAdjustmentForm.entryType}
                  onChange={(e) => setLedgerAdjustmentForm({ ...ledgerAdjustmentForm, entryType: e.target.value })}
                  required
                >
                  <option value="Debit">Debit (Increase Assets/Expenses)</option>
                  <option value="Credit">Credit (Increase Liabilities/Equity/Revenue)</option>
                </select>
              </div>
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Reason for Adjustment * (min 10 characters)</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Explain why this adjustment is needed..."
                  value={ledgerAdjustmentForm.reason}
                  onChange={(e) => setLedgerAdjustmentForm({ ...ledgerAdjustmentForm, reason: e.target.value })}
                  required
                  minLength={10}
                />
              </div>
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Additional Notes</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Any additional notes..."
                  value={ledgerAdjustmentForm.notes}
                  onChange={(e) => setLedgerAdjustmentForm({ ...ledgerAdjustmentForm, notes: e.target.value })}
                />
              </div>
            </div>
            <button type="submit" style={{ ...styles.submitBtn, marginTop: "20px" }} disabled={loading}>
              {loading ? "Processing..." : "📝 Post Adjustment"}
            </button>
          </form>
        </div>
      )}

      {/* Reversal Modal */}
      {showReversalModal && selectedItem && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🔄 Reverse {reversalType.charAt(0).toUpperCase() + reversalType.slice(1)}</h3>
            
            <div style={{ background: "#f9fafb", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
              <p><strong>Reference:</strong> {selectedItem.reference || selectedItem.id}</p>
              <p><strong>Amount:</strong> <span style={{ color: "#dc2626", fontWeight: "bold" }}>{formatCurrency(selectedItem.amount)}</span></p>
              <p><strong>Description:</strong> {selectedItem.description}</p>
              {selectedItem.transactionType && <p><strong>Type:</strong> {selectedItem.transactionType}</p>}
              {selectedItem.category && <p><strong>Category:</strong> {selectedItem.category}</p>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Reason for Reversal * (min 10 characters)</label>
              <textarea
                style={styles.textarea}
                placeholder="Explain why this transaction needs to be reversed..."
                value={reversalForm.reason}
                onChange={(e) => setReversalForm({ ...reversalForm, reason: e.target.value })}
                required
                minLength={10}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Additional Notes</label>
              <textarea
                style={{ ...styles.textarea, minHeight: "60px" }}
                placeholder="Any additional notes..."
                value={reversalForm.notes}
                onChange={(e) => setReversalForm({ ...reversalForm, notes: e.target.value })}
              />
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowReversalModal(false)}>Cancel</button>
              <button style={styles.confirmBtn} onClick={handleReversal} disabled={loading}>
                {loading ? "Processing..." : "Confirm Reversal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

